// Quote documents (mock M7) where they meet the rest of Omni: the upload sheet New quote's
// From a quote document opens, the Quote documents card on Quotes, and the notice the quote
// builder shows right after Confirm creates a draft.
import React, { useRef, useState } from "react";
import { INTAKE_ACCEPT, INTAKE_ERRORS, INTAKE_LIMITS, type Dealer, type Quote, type QuoteIntakeState, type QuoteIntakeSummary, type Via } from "@phoneware/lens-domain";
import { ApiError } from "../api.js";
import { MAX_INTAKE_BYTES, confirmationFor, fileSize, intakeView, joinAnd, sourceKind, sourceLabel, viaLine } from "../api-intakes.js";
import { Icon } from "../icons.js";
import { Link, navigate } from "../router.js";
import { useLoad, useSession } from "../session.js";
import { errorText, when, whenIn } from "../format.js";
import { Sheet, useToast } from "../ui.js";

/** A file as a small page icon with its type on it. */
export function FileIcon({ name }: { name: string }): React.ReactElement {
  const kind = (/\.([a-z0-9]{1,4})$/i.exec(name)?.[1] ?? "PDF").toUpperCase();
  return (
    <span className={kind === "PDF" ? "fi" : "fi other"} aria-hidden="true">
      {kind}
    </span>
  );
}

/** Where a quote document stands, as a chip. */
export function IntakeChip({ intake }: { intake: { state: QuoteIntakeState; extraction?: { result: { lines: readonly unknown[] } } | null; lineCount?: number | null } }): React.ReactElement {
  const view = intakeView(intake);
  if (view === "extracting") {
    return (
      <span className="chip azure" data-status={view}>
        <Icon name="loader-circle" size={10} className="spin" />
        Reading
      </span>
    );
  }
  const look: Record<Exclude<typeof view, "extracting">, [string, string]> = {
    ready: ["chip azure", "Ready to check"],
    failed: ["chip stop", "Couldn\u2019t read it"],
    nolines: ["chip warn", "No lines found"],
    confirmed: ["chip ok", "Confirmed"],
    discarded: ["chip quiet", "Discarded"],
  };
  return (
    <span className={look[view][0]} data-status={view}>
      {look[view][1]}
    </span>
  );
}

/** "Sent by Claude for Marisol Quintero" (with the bot mark), or "Uploaded by you". */
export function ViaText({ intake, name, chip }: { intake: { createdVia: Via; createdBy: string }; name: (email: string) => string; chip?: boolean }): React.ReactElement {
  const { me } = useSession();
  const text = viaLine(intake.createdVia, intake.createdBy, name, me.user.email);
  if (intake.createdVia.kind === "web") return <span>{text}</span>;
  return (
    <span className={chip ? "chip sent" : "via-l"} data-testid="intake-via">
      <Icon name="bot" size={11} />
      {text}
    </span>
  );
}

/** Quote documents on the Quotes tab: every one not confirmed or discarded yet. */
export function QuoteDocumentsCard({ intakes, name, dealers }: { intakes: QuoteIntakeSummary[]; name: (email: string) => string; dealers: Dealer[] | null }): React.ReactElement {
  return (
    <section className="card docs-card" data-testid="quote-documents">
      <div className="card-h">
        <h2>Quote documents</h2>
        <span className="note">Nothing is created from one until someone checks it and confirms.</span>
      </div>
      <ul className="rows">
        {intakes.map((ik) => (
          <li key={ik.id}>
            <Link className="docrow filecard" to={`/quotes/intake/${encodeURIComponent(ik.id)}`} data-testid={`quote-document-${ik.number}`} data-kind={sourceKind(ik.source)}>
              <FileIcon name={sourceLabel(ik.source)} />
              <span className="fn">
                <b>{sourceLabel(ik.source)}</b>
                <span>
                  {ik.number}
                  {dealers ? ` \u00b7 ${dealers.find((d) => d.id === ik.dealerId)?.name ?? ""}` : ""} · <ViaText intake={ik} name={name} /> · {when(ik.createdAt)}
                </span>
              </span>
              <IntakeChip intake={ik} />
              <Icon name="chevron-right" size={16} className="chev" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

type UploadTab = "file" | "paste" | "kqm";
type UploadState =
  | { phase: "pick" }
  | { phase: "busy"; name: string; size: number }
  | { phase: "refused"; reason: "size" | "type"; name: string; size: number; message: string }
  | { phase: "failed"; name: string; size: number; message: string };

/**
 * New quote, From a quote document: drop or choose a PDF, and it goes to Omni as it is. A file over 10 MB is refused
 * before it is sent; the server refuses one that isn't a PDF. The same file again within a day opens the first reading.
 * Phoneware says whose quote it is first: any dealer for a Super admin, the house dealer for Sales at Phoneware.
 */
export function UploadSheet({ onClose }: { onClose: () => void }): React.ReactElement {
  const { me, api, everyDealer, canWrite } = useSession();
  const toast = useToast();
  const ops = everyDealer;
  const loadedDealers = useLoad<Dealer[]>(() => (ops ? api.dealers() : Promise.resolve([])), [ops]);
  // Only the dealers this person may quote for: every one for a Super admin, the house dealer for Sales at Phoneware.
  const dealers = { data: loadedDealers.data?.filter((d) => canWrite("quotes.write", d)) ?? null };
  const [dealerId, setDealerId] = useState("");
  const [state, setState] = useState<UploadState>({ phase: "pick" });
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const dealerName = ops ? dealers.data?.find((d) => d.id === dealerId)?.name ?? "the dealer" : me.dealer?.name ?? "your dealer";
  const canPick = !ops || Boolean(dealerId);

  const { can } = useSession();
  const [tab, setTab] = useState<UploadTab>("file");
  const [paste, setPaste] = useState("");
  const [transcript, setTranscript] = useState(false);
  const [title, setTitle] = useState("");
  const [kqm, setKqm] = useState("");

  async function send(file: File): Promise<void> {
    const card = { name: file.name, size: file.size };
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    if (ext === "heic" || ext === "heif" || file.type === "image/heic" || file.type === "image/heif") {
      setState({ phase: "refused", reason: "type", message: INTAKE_ERRORS.heic, ...card });
      return;
    }
    if (ext === "msg" || file.type === "application/vnd.ms-outlook") {
      setState({ phase: "refused", reason: "type", message: INTAKE_ERRORS.outlookMsg, ...card });
      return;
    }
    if (file.size > MAX_INTAKE_BYTES) {
      setState({ phase: "refused", reason: "size", message: "Omni takes files up to 10 MB. Save a smaller copy of the quote and upload that.", ...card });
      return;
    }
    setState({ phase: "busy", ...card });
    try {
      const { intake, again } = await api.uploadQuoteIntake(file, file.name, ops ? dealerId : undefined);
      onClose();
      navigate(`/quotes/intake/${encodeURIComponent(intake.id)}`);
      if (again) toast(`${dealerName} uploaded this file ${whenIn(intake.createdAt)}, so here is that reading again (${intake.number}).`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 413) {
        setState({ phase: "refused", reason: "size", message: "Omni takes files up to 10 MB. Save a smaller copy of the quote and upload that.", ...card });
      } else if (err instanceof ApiError && err.status === 415) {
        setState({ phase: "refused", reason: "type", message: err.message, ...card });
      } else setState({ phase: "failed", ...card, message: errorText(err) });
    }
  }

  async function sendText(): Promise<void> {
    if (paste.length > INTAKE_LIMITS.textChars) {
      setState({ phase: "refused", reason: "size", name: title || "Pasted text", size: paste.length, message: INTAKE_ERRORS.textTooLong });
      return;
    }
    setState({ phase: "busy", name: title || "Pasted text", size: paste.length });
    try {
      const { intake } = await api.submitQuoteText({ text: paste, textKind: transcript ? "transcript" : "document", title: title.trim() || null, dealerId: ops ? dealerId : undefined });
      onClose();
      navigate(`/quotes/intake/${encodeURIComponent(intake.id)}`);
    } catch (err) {
      setState({ phase: "failed", name: title || "Pasted text", size: paste.length, message: errorText(err) });
    }
  }

  async function sendKqm(): Promise<void> {
    setState({ phase: "busy", name: "KQM quote", size: 0 });
    try {
      const { intake } = await api.importKqmQuote({ quote: kqm.trim(), dealerId: ops ? dealerId : undefined });
      onClose();
      navigate(`/quotes/intake/${encodeURIComponent(intake.id)}`);
    } catch (err) {
      setState({ phase: "failed", name: "KQM quote", size: 0, message: errorText(err) });
    }
  }

  const fileCard =
    state.phase === "pick" ? null : (
      <div className="filecard">
        <FileIcon name={state.name} />
        <span className="fn">
          <b>{state.name}</b>
          <span>{fileSize(state.size)}</span>
        </span>
      </div>
    );
  const again = (
    <div className="btnrow">
      <button className="btn small white" type="button" data-testid="upload-again" onClick={() => setState({ phase: "pick" })}>
        <Icon name="refresh-cw" size={13} />
        Try again
      </button>
    </div>
  );

  return (
    <Sheet
      title="From a quote document"
      onClose={onClose}
      foot={
        <button className="btn" type="button" onClick={onClose}>
          {state.phase === "refused" ? "Close" : "Cancel"}
        </button>
      }
    >
      <p className="upintro">
        Give Omni the quote. It reads the customer, contacts, term and lines, and matches them to {dealerName}&rsquo;s customers and catalog. You check all of it before anything is created.
      </p>
      {ops ? (
        <label className="field up-dealer">
          <span>Whose quote is it?</span>
          <select className="input" data-testid="upload-dealer" value={dealerId} onChange={(e) => setDealerId(e.target.value)} disabled={state.phase === "busy"}>
            <option value="">Pick the dealer</option>
            {(dealers.data ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <div className="uptabs" role="tablist" aria-label="How the quote arrives" data-testid="upload-sheet">
        <button type="button" role="tab" data-testid="tab-file" aria-selected={tab === "file"} onClick={() => setTab("file")}>File</button>
        <button type="button" role="tab" data-testid="tab-paste" aria-selected={tab === "paste"} onClick={() => setTab("paste")}>Paste</button>
        {can("quote_intakes.kqm") ? <button type="button" role="tab" data-testid="tab-kqm" aria-selected={tab === "kqm"} onClick={() => setTab("kqm")}>KQM</button> : null}
      </div>
      {tab === "paste" && state.phase === "pick" ? (
        <div data-testid="paste-pane">
          <label className="field"><span>The quote</span><textarea className="input" data-testid="paste-text" value={paste} onChange={(e) => setPaste(e.target.value)} rows={8} /></label>
          <label className="checkline"><input type="checkbox" data-testid="paste-kind" checked={transcript} onChange={(e) => setTranscript(e.target.checked)} /> It&rsquo;s a conversation transcript</label>
          <p className="upnote">A transcript leaves out anything declined, only floated or put off.</p>
          <label className="field"><span>Title, optional</span><input className="input" data-testid="paste-title" value={title} onChange={(e) => setTitle(e.target.value)} /></label>
          <p className="upnote" data-testid="paste-count">{paste.length.toLocaleString("en-US")} of {INTAKE_LIMITS.textChars.toLocaleString("en-US")} characters</p>
          <button className="btn primary" type="button" data-testid="paste-read" disabled={!canPick || !paste.trim()} onClick={() => void sendText()}>Read it</button>
        </div>
      ) : null}
      {tab === "kqm" && state.phase === "pick" ? (
        <div data-testid="kqm-pane">
          <p className="upnote">Only Phoneware staff can import from KQM. Omni never changes anything in KQM.</p>
          <label className="field"><span>KQM quote link, id or number</span><input className="input" data-testid="kqm-q" value={kqm} onChange={(e) => setKqm(e.target.value)} /></label>
          <button className="btn primary" type="button" data-testid="kqm-import" disabled={!canPick || !kqm.trim()} onClick={() => void sendKqm()}>Import from KQM</button>
        </div>
      ) : null}
      {state.phase === "busy" ? (
        <div className="drop busy" data-testid="upload-busy">
          {fileCard}
          <div className="prog">
            <i />
          </div>
          <span className="rules">Uploading</span>
        </div>
      ) : state.phase === "refused" ? (
        <>
          <div className="drop busy">{fileCard}</div>
          <div className="callout stop" role="alert" data-testid={state.reason === "size" ? "upload-refused-size" : "upload-refused-type"}>
            <Icon name="ban" size={15} />
            <div className="cbody">
              <b>{state.reason === "size" ? `${state.name} is ${fileSize(state.size)}.` : state.name}</b>
              {state.message || (state.reason === "size" ? "Omni takes files up to 10 MB." : INTAKE_ERRORS.unsupportedType)}
              {again}
            </div>
          </div>
        </>
      ) : state.phase === "failed" ? (
        <>
          <div className="drop busy">{fileCard}</div>
          <div className="callout stop" role="alert" data-testid="upload-failed">
            <Icon name="triangle-alert" size={15} />
            <div className="cbody">
              <b>{state.name} didn&rsquo;t upload.</b>
              {state.message}
              {again}
            </div>
          </div>
        </>
      ) : tab === "file" ? (
        <>
          <div
            className={over ? "drop over" : "drop"}
            data-testid="upload-drop"
            onDragOver={(e) => {
              e.preventDefault();
              if (canPick) setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(false);
              const file = e.dataTransfer.files[0];
              if (file && canPick) void send(file);
            }}
          >
            <span className="mark">
              <Icon name="file-up" size={22} />
            </span>
            <b>Drop the quote here</b>
            <span className="rules">PDF, photos (PNG, JPEG, WebP, GIF), Excel or CSV, Word, email (.eml), web pages and text files, up to 10 MB</span>
            <button className="btn primary" type="button" data-testid="upload-choose" disabled={!canPick} onClick={() => input.current?.click()}>
              <Icon name="search" size={14} />
              Choose a file
            </button>
            <input
              ref={input}
              className="sr"
              type="file"
              accept={INTAKE_ACCEPT}
              tabIndex={-1}
              aria-hidden="true"
              data-testid="upload-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) void send(file);
              }}
            />
          </div>
          <p className="upnote">
            Using Claude or ChatGPT? Once it&rsquo;s connected in Settings, under Connect an AI or app, it can send a quote PDF here too. It lands under Quote documents
            for you to check.
          </p>
        </>
      ) : null}
    </Sheet>
  );
}

/** Right after Confirm, in the draft it made: what was created from which file, and the way back to the PDF. */
export function IntakeCreatedNotice({ quote }: { quote: Quote }): React.ReactElement | null {
  const c = confirmationFor(quote.id);
  if (!c) return null;
  return (
    <div className="callout ok intake-made" data-testid="intake-created">
      <Icon name="circle-check" size={15} />
      <div className="cbody">
        <b>{c.needsApproval ? `${quote.number} created` : `Draft ${quote.number} created`}</b>
        <p>
          From {c.filename} ({c.intakeNumber}). The lines, quantities, prices, term and contact are the ones you confirmed, and the PDF stays with the quote.
        </p>
        {c.newCustomer ? (
          <p>
            {c.customerName} is in Autotask now as a Prospect under {c.dealerName}
            {c.accountNumber ? (
              <>
                , account number <b>{c.accountNumber}</b>
              </>
            ) : null}
            {c.added.length ? `, with ${joinAnd(c.added)} as ${c.added.length === 1 ? "its contact" : "contacts"}` : ""}.
          </p>
        ) : c.added.length ? (
          <p>
            {joinAnd(c.added)} {c.added.length === 1 ? "is now a contact" : "are now contacts"} at {c.customerName}.
          </p>
        ) : null}
        {c.needsApproval ? (
          <p>
            {c.belowFloor === 1 ? "One line is" : `${c.belowFloor} lines are`} below the floor, so it&rsquo;s with Phoneware for approval. You send the QuoteLink once they
            approve.
          </p>
        ) : null}
        <div className="btnrow">
          <Link className="btn small white" to={`/quotes/intake/${encodeURIComponent(c.intakeId)}`} data-testid="intake-created-pdf">
            <Icon name="file-text" size={13} />
            See the PDF
          </Link>
        </div>
      </div>
    </div>
  );
}
